import React, { useState } from 'react';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';

interface TestResult {
  success: boolean;
  models?: string[];
  assistant?: {
    success: boolean;
    id: string;
    error?: string;
  };
  error?: string;
  details?: string;
}

export function TestOpenAI() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTest = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-openai`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Error testing OpenAI API:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-lg mx-auto">
      <h2 className="text-xl font-semibold mb-4">OpenAI API Connection Test</h2>
      
      <div className="mb-4">
        <p className="text-gray-600 mb-2">
          Test the OpenAI API connection to verify that your helpdesk function can connect to the API.
        </p>
        <button
          onClick={handleTest}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Testing...
            </>
          ) : (
            'Test OpenAI Connection'
          )}
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 rounded-lg flex items-center gap-2 text-red-700 mb-4">
          <AlertCircle className="flex-shrink-0 w-5 h-5" />
          <div>
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {result && (
        <div className={`p-4 ${result.success ? 'bg-green-50' : 'bg-red-50'} rounded-lg mb-4`}>
          <div className="flex items-center gap-2 mb-2">
            {result.success ? (
              <CheckCircle className="text-green-600 w-5 h-5" />
            ) : (
              <AlertCircle className="text-red-600 w-5 h-5" />
            )}
            <p className={`font-medium ${result.success ? 'text-green-700' : 'text-red-700'}`}>
              {result.success ? 'Connection Successful' : 'Connection Failed'}
            </p>
          </div>
          
          {result.success && result.models && (
            <div className="mb-3">
              <p className="text-sm font-medium mb-1 text-gray-700">Available Models:</p>
              <div className="bg-white p-2 rounded-md text-sm max-h-24 overflow-y-auto">
                <ul className="list-disc list-inside">
                  {result.models.slice(0, 5).map((model, idx) => (
                    <li key={idx} className="text-gray-600">{model}</li>
                  ))}
                  {result.models.length > 5 && (
                    <li className="text-gray-500">{result.models.length - 5} more...</li>
                  )}
                </ul>
              </div>
            </div>
          )}
          
          {result.assistant && (
            <div className="mb-3">
              <p className="text-sm font-medium mb-1 text-gray-700">Assistant Check:</p>
              <div className={`p-2 rounded-md text-sm ${result.assistant.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {result.assistant.success ? (
                  <>Assistant found: {result.assistant.id}</>
                ) : (
                  <>Assistant error: {result.assistant.error}</>
                )}
              </div>
            </div>
          )}
          
          {result.error && (
            <div className="bg-white p-2 rounded-md text-sm text-red-600 mb-3">
              <p className="font-medium">Error Message:</p>
              <p className="whitespace-pre-wrap">{result.error}</p>
            </div>
          )}
          
          {result.details && (
            <details className="mt-2">
              <summary className="text-sm cursor-pointer text-gray-700">Technical Details</summary>
              <pre className="mt-2 text-xs bg-gray-800 text-gray-200 p-2 rounded overflow-auto max-h-60">
                {typeof result.details === 'string' ? result.details : JSON.stringify(result.details, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
      
      <div className="text-sm text-gray-500">
        <p>This test checks:</p>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li>Connection to OpenAI API</li>
          <li>Valid API key</li>
          <li>Access to the helpdesk assistant</li>
          <li>Basic message functionality</li>
        </ul>
      </div>
    </div>
  );
}